import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "components/Button";
import { CodeBlock } from "components/CodeBlock/CodeBlock";
import CodeInput from "components/FormControls/CharacterInputBoxes/NumericCodeInput";
import { CircularCenteredLoader } from "components/Loaders";
import { TextError } from "components/Notifications";
import endpointMaker from "configuration/endpoints";
import { getUrlQueryParam } from "helpers";
import request from "http/request";
import QRCode from "qrcode";
import React from "react";
import { actions } from "redux/auth/authSlice";
import { useAppDispatch } from "redux/store";
import { whiteColor } from "vendor/colors";

type IProps = {
  onSetup?: () => void;
  setDialogOpened: (o: boolean) => void;
};
function SetupMFA({ setDialogOpened, onSetup }: IProps) {
  const dispatch = useAppDispatch();
  const [qrUrl, setQrUrl] = React.useState<string>("");
  const [error, setError] = React.useState<string>();
  const [code, setCode] = React.useState<string>();
  const [isValidating, setIsValidating] = React.useState<boolean>();
  const [secretCode, setSecretCode] = React.useState<any>("");

  React.useEffect(() => {
    request
      .getJson(endpointMaker.account.mfaSetupStart())
      .then((resp) => {
        setSecretCode(getUrlQueryParam(resp?.mafSetupUri, "secret"));

        return QRCode.toDataURL(resp?.mafSetupUri)
          .then(setQrUrl)
          .catch(setError);
      })
      .catch((e) => setError(e?.message ?? "unknown error"));

    return () => {
      request.post(endpointMaker.account.mfaSetupCancel()).catch(() => {
        //allow silent fail
      });
    };
  }, []);

  const onConfirm = () => {
    setIsValidating(true);
    request
      .post(endpointMaker.account.mfaSetupConfirm(), {
        mfaCode: code,
      })
      .then(() => {
        setDialogOpened(false);
        dispatch(actions.setupMFA());
        onSetup?.();
      })
      .catch((e) => {
        setIsValidating(false);
        setError(`MFA is not set: ${e?.message}`);
      });
  };
  return (
    <Dialog
      open={true}
      onClose={() => setDialogOpened(false)}
      aria-labelledby="setup MFA">
      <DialogTitle>Setup MFA</DialogTitle>
      <DialogContent>
        {error && <TextError message={error} />}
        <DialogContentText>
          Using an authenticator app, scan qr code below, and provide 6-digit
          verification code
        </DialogContentText>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "25px",
          }}>
          <div style={{ width: 212, height: 212 }}>
            {qrUrl ? (
              <img
                data-qa="mfa-qr-code"
                src={qrUrl}
                alt="MFA setup qr code"
                style={{
                  display: "block",
                  maxWidth: "100%",
                  maxHeight: "100%",
                }}
              />
            ) : (
              <CircularCenteredLoader />
            )}
          </div>

          <CodeBlock code={secretCode} />

          <CodeInput
            length={6}
            value={code}
            setValue={setCode}
            onChange={() => setError("")}
          />
        </div>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => setDialogOpened(false)}
          color="primary"
          data-qa="dialog_cancel">
          Cancel
        </Button>
        <Button
          disabled={isValidating}
          onClick={onConfirm}
          color="primary"
          data-qa="dialog_confirm">
          Confirm
          {isValidating ? (
            <div
              style={{ marginLeft: 8, width: 18, height: 18, display: "flex" }}>
              <CircularCenteredLoader
                size={18}
                color={whiteColor[0]}
              />
            </div>
          ) : null}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SetupMFA;
